import React, { useEffect } from "react";
import Lenis from "lenis";
import { useReducedMotion } from "framer-motion";

/**
 * LenisProvider
 * -----------------------------------------------------------------------------
 * Initialises Lenis smooth scroll at the app root and drives its RAF loop.
 *
 * Tuning per Phase 5 brief:
 *   - duration: 1.2
 *   - easing:   exponential-out (Math.min(1, 1.001 - 2^(-10 t)))
 *   - wheelMultiplier: 1.0 (no amplification)
 *   - smoothWheel: true on desktop, false under prefers-reduced-motion
 *   - smoothTouch: false (native scroll on mobile — smooth-on-touch feels
 *     laggy on phones and interferes with over-scroll gestures)
 *
 * Lenis 1.x already dispatches native `scroll` events on `window`, so
 * framer-motion's `useScroll` picks up progress correctly without any
 * bridging code.
 */
export function LenisProvider({ children }) {
  const reduce = useReducedMotion();

  useEffect(() => {
    if (reduce) return undefined;

    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      wheelMultiplier: 1.0,
      smoothWheel: true,
      smoothTouch: false,
      touchMultiplier: 1.0,
    });

    // Expose instance for imperative scroll (e.g. filter → panel jumps).
    // Non-null only while smooth scroll is active — components fall back
    // to native scrollIntoView under prefers-reduced-motion.
    if (typeof window !== "undefined") {
      window.__lenis = lenis;
    }

    let rafId = 0;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);

    return () => {
      cancelAnimationFrame(rafId);
      if (typeof window !== "undefined" && window.__lenis === lenis) {
        window.__lenis = null;
      }
      lenis.destroy();
    };
  }, [reduce]);

  return <>{children}</>;
}

export default LenisProvider;
